import { useEffect, useMemo, useState } from "react";
import { ArrowRight, LayoutGrid, ShieldCheck, Server, RefreshCw, Headphones } from "lucide-react";
import { Helmet } from "react-helmet-async";

const Hero = () => {
  const slides = useMemo(
    () => [
      {
        badge: "Trusted ICT Partner",
        titleA: "Modern ICT that",
        titleB: "just works",
        sub: "Reliable infrastructure, support, and systems that keep your business running without the stress.",
      },
      {
        badge: "Security & Monitoring",
        titleA: "Security built for",
        titleB: "peace of mind",
        sub: "Protect your people, spaces, and systems with practical security setups and 24/7 monitoring.",
      },
      {
        badge: "Digital & E-Procurement",
        titleA: "Smart systems that",
        titleB: "move faster",
        sub: "From digital workflows to e-procurement — streamline operations and reduce friction across teams.",
      },
    ],
    []
  );

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIndex((i) => (i + 1) % slides.length), 5000);
    return () => clearInterval(t);
  }, [paused, slides.length]);

  const cards = [
    {
      icon: ShieldCheck,
      label: "Security Systems",
      desc: "CCTV, access control and audit-ready setups.",
      color: "text-indigo-600 bg-indigo-100",
    },
    {
      icon: Server,
      label: "ICT Infrastructure",
      desc: "Networks, servers, and endpoint support.",
      color: "text-teal-600 bg-teal-100",
    },
    {
      icon: RefreshCw,
      label: "E-Procurement",
      desc: "Digital workflows that reduce friction.",
      color: "text-violet-600 bg-violet-100",
    },
    {
      icon: Headphones,
      label: "24/7 Support",
      desc: "Reliable oversight for critical systems.",
      color: "text-amber-600 bg-amber-100",
    },
  ];

  return (
    <section
      className="relative min-h-screen bg-violet-50 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Helmet>
        <title>Northstart Systems | ICT & Security Solutions in Kenya</title>
        <meta
          name="description"
          content="Northstart Systems delivers ICT, e-procurement, and security solutions across Kenya and beyond."
        />
        <link rel="canonical" href="https://northstartsystems.com/" />
      </Helmet>

      {/* Subtle grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px), linear-gradient(to right, rgba(0,0,0,0.05) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-4 pt-28 pb-20 min-h-screen grid lg:grid-cols-2 gap-12 items-center">

        {/* LEFT */}
        <div>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-indigo-100 border border-indigo-200 px-4 py-1.5 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
            <span className="text-xs font-semibold text-indigo-600 tracking-wide">
              {slides[index].badge}
            </span>
          </div>

          {/* Title */}
          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-700 ease-out"
              style={{ transform: `translateX(-${index * 100}%)` }}
            >
              {slides.map((s, i) => (
                <div key={i} className="min-w-full">
                  <h1 className="text-5xl md:text-6xl lg:text-[64px] font-extrabold text-[#1e1642] leading-[1.08] tracking-tight">
                    {s.titleA}
                    <br />
                    <span className="text-indigo-500">{s.titleB}</span>
                  </h1>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-6 text-base text-slate-500 leading-relaxed max-w-md">
            {slides[index].sub}
          </p>

          {/* CTAs */}
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <button
              onClick={() =>
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold px-6 py-3.5 rounded-xl transition"
            >
              Talk to us
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })
              }
              className="inline-flex items-center gap-2 text-slate-600 hover:text-indigo-600 text-sm font-semibold px-5 py-3.5 rounded-xl border border-slate-200 hover:border-indigo-300 transition"
            >
              <LayoutGrid className="w-4 h-4" />
              View services
            </button>
          </div>

          {/* Trust line */}
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            {["Fast response", "Clear communication", "Solid delivery"].map((t, i, a) => (
              <span key={t} className="flex items-center gap-3">
                <span className="text-xs text-slate-400 font-medium">{t}</span>
                {i < a.length - 1 && (
                  <span className="text-slate-200 text-xs">·</span>
                )}
              </span>
            ))}
          </div>

          {/* Slide dots */}
          <div className="mt-8 flex items-center gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`h-[3px] rounded-full transition-all duration-300 ${
                  i === index ? "w-6 bg-indigo-500" : "w-2 bg-slate-300"
                }`}
              />
            ))}
          </div>
        </div>

        {/* RIGHT: Feature cards */}
        <div className="grid grid-cols-2 gap-4">
          {cards.map(({ icon: Icon, label, desc, color }) => (
            <div
              key={label}
              className="bg-white border border-slate-100 rounded-2xl p-5 flex flex-col gap-3 hover:border-indigo-100 hover:bg-indigo-50/50 transition"
            >
              <div
                className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{label}</p>
                <p className="text-xs text-slate-400 leading-relaxed mt-1">{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;